// src/pages/BalanceSheet/hooks/useBalanceSheetData.js
import { useSelector } from "react-redux";
import { selectAuthInfo } from "../../../Auth/authSelectors";


export const useBalanceSheetData = () => {
  const { balances = {} } = useSelector((state) => state.balanceSheet || {});
  const authInfo  = useSelector(selectAuthInfo);
  const isIndia   = authInfo?.country?.toLowerCase() === "india";
  const taxLabel  = isIndia ? "GST" : "VAT";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;
  const currency  = authInfo?.currency ?? "";

  const suspense  = Number(balances.suspenseBalance || 0);
  const diffValue = Number(balances.diffOpeningBalance || 0);
const balancingAdjustment = Math.abs(Number(balances.balancingAdjustment || 0));
  const currentAssets = [
    { key: "section_ca",              label: "Current Assets",                              isSection: true },
    { key: "cashBalance",             label: "Cash In Hand",                                value: balances.cashBalance },
    { key: "bankBalance",             label: "Bank Accounts Balance",                       value: balances.bankBalance },
    { key: "debtorBalance",           label: "Customer Due Balance",                        value: balances.debtorBalance },
    { key: "advanceCreditorBalance",  label: "Advance Payment to Supplier",                 value: balances.advanceCreditorBalance },
    { key: "inventoryBalance",        label: "Closing Product Inventory/ Stock Value",      value: balances.inventoryBalance },
    { key: "currentAssetsBalance",    label: "Current Assets",                              value: balances.currentAssetsBalance },
    { key: "depositAccountBalance",   label: "Deposit Account",                             value: balances.depositAccountBalance },
    ...(suspense > 0 ? [{ key: "suspenseBalance", label: "Suspense Account", value: suspense }] : []),
    {
      key: "totalCA", label: "Total Current Assets", strong: true,
      value:
        (balances.cashBalance ?? 0) + (balances.bankBalance ?? 0) +
        (balances.debtorBalance ?? 0) + (balances.advanceCreditorBalance ?? 0) +
        (balances.inventoryBalance ?? 0) + (balances.currentAssetsBalance ?? 0) +
        (balances.depositAccountBalance ?? 0) + (suspense > 0 ? suspense : 0),
    },
  ];

  const nonCurrentAssets = [
    { key: "section_nca",            label: "Non-Current Assets",                          isSection: true },
    { key: "fixedAssestsBalance",    label: "Fixed Assets",                                value: balances.fixedAssestsBalance },
    { key: "intangibleAssetBalance", label: "Intangible Assets",                           value: balances.intangibleAssetBalance },
    { key: "investmentBalance",      label: "Investments",                                 value: balances.investmentBalance },
    { key: "receiveableBranchAmount",label: "Receivable From Other Branches",              value: balances.receiveableBranchAmount },
    { key: "productTransferBalance", label: "Transfer Product Value To Other Branches",    value: balances.productTransferBalance },
    {
      key: "totalNCA", label: "Total Non-Current Assets", strong: true,
      value:
        (balances.fixedAssestsBalance ?? 0) + (balances.investmentBalance ?? 0) +
        (balances.receiveableBranchAmount ?? 0) + (balances.productTransferBalance ?? 0),
    },
  ];

  const assets = [
    ...currentAssets,
    ...nonCurrentAssets,
    { key: "assetsTotal", label: "Assets (Total)", value: balances.assetsTotal, strong: true },
  ];

  const currentLiabilities = [
    { key: "section_cl",                      label: "Current Liabilities",                    isSection: true },
    { key: "creditorBalance",                  label: "Supplier Due Balance",                   value: balances.creditorBalance },
    { key: "advanceDebtorBalance",             label: "Advance Received From Customer",         value: balances.advanceDebtorBalance },
    ...(showTax ? [{ key: "taxBalance", label: `${taxLabel} Account`, value: balances.taxBalance }] : []),
    { key: "currentLiabilityBalance",          label: "Current Liability Balance",              value: balances.currentLiabilityBalance },
    { key: "productTransferReceivedBalance",   label: "Received Product Value From Branches",   value: balances.productTransferReceivedBalance },
    { key: "diffOpeningBalance",              label: "Difference In Opening",                  value: balances.diffOpeningBalance },
    ...(suspense < 0 ? [{ key: "suspenseBalance", label: "Suspense Account", value: Math.abs(suspense) }] : []),
    {
      key: "totalCL", label: "Total Current Liabilities", strong: true,
      value:
        (balances.creditorBalance ?? 0) + (balances.advanceDebtorBalance ?? 0) +
        (balances.taxBalance ?? 0) + (balances.currentLiabilityBalance ?? 0) +
        (balances.productTransferReceivedBalance ?? 0) +
        (balances.diffOpeningBalance ?? 0) +
        (suspense < 0 ? Math.abs(suspense) : 0),
    },
  ];

  const nonCurrentLiabilities = [
    { key: "section_ncl",  label: "Non-Current Liabilities",       isSection: true },
    { key: "loanBalance",  label: "Loan (Liability)",               value: balances.loanBalance },
    { key: "totalNCL",     label: "Total Non-Current Liabilities",  value: balances.loanBalance ?? 0, strong: true },
  ];

const equity = [
  { key: "section_eq",            label: "Equity",                  isSection: true },
  { key: "capitalBalance",        label: "Capital Account",         value: balances.capitalBalance },
  { key: "reserveSurplusBalance", label: "Reserve Surplus Account", value: balances.reserveSurplusBalance },
  { key: "retainedEarnings",      label: "Retained Earnings",       value: balances.retainedEarnings },
  { key: "profitLossBalance",     label: "Profit/ Loss",            value: balances.profitLossBalance },

  ...(Number(balancingAdjustment.toFixed(2)) > 0
    ? [{ key: "balancingAdjustment", label: "Balancing Adjustment", value: balancingAdjustment }]
    : []),
  {
    key: "totalEq", label: "Total Equity", strong: true,
    value:
      (balances.capitalBalance ?? 0) + (balances.reserveSurplusBalance ?? 0) +
      (balances.retainedEarnings ?? 0) + (balances.profitLossBalance ?? 0),
  },
];

  const liabilities = [
    ...currentLiabilities,
    ...nonCurrentLiabilities,
    ...equity,
    { key: "liabilityTotal", label: "Liabilities (Total)", value: balances.liabilityTotal, strong: true },
  ];

  return { liabilities, assets, currency, balances };
};